import {
	FormControlLabel,
	Grid,
	Paper,
	Switch,
	Typography,
} from "@mui/material";
import { get } from "lodash-es";
import React, { useEffect, useMemo, useState } from "react";
import { ErrorModal } from "../../components/modals/error-modal";
import { HelmetDexteel } from "../../helmet/HelmetDexteel";
import type { spParameter } from "../../services";
import { DynamicParameterForm } from "./components/DynamicParameterForm";
import { PinGate } from "./components/PinGate";
import { ResultsList } from "./components/ResultsList";
import { SPAutocomplete } from "./components/SPAutocomplete";
import type {
	ExecutionResult,
	ParameterFormState,
	SPParameter,
	StoredProcedure,
} from "./models/SPExecutorModels";
import {
	executeStoredProcedure,
	getStoredProceduresWithParameters,
} from "./repositories/SPExecutorRepository";

const BACKEND_MANAGED_PARAMS = ["@user"];

export const SPExecutorPage = () => {
	const [procedures, setProcedures] = useState<StoredProcedure[]>([]);
	const [allParameters, setAllParameters] = useState<SPParameter[]>([]);
	const [selectedProcedure, setSelectedProcedure] =
		useState<StoredProcedure | null>(null);
	const [executionResults, setExecutionResults] = useState<ExecutionResult[]>(
		[],
	);
	const [isLoading, setIsLoading] = useState(false);
	const [isExecuting, setIsExecuting] = useState(false);
	const [error, setError] = useState("");
	const [accumulateResults, setAccumulateResults] = useState(true);

	const loadData = async () => {
		setIsLoading(true);
		setError("");
		const resp = await getStoredProceduresWithParameters();
		if (resp.ok) {
			const procs = get(resp, "data.tables[0].rows", []) as StoredProcedure[];
			const params = get(resp, "data.tables[1].rows", []) as SPParameter[];
			setProcedures(procs);
			setAllParameters(params);
		} else {
			setError(resp.message);
		}
		setIsLoading(false);
	};

	useEffect(() => {
		loadData();
	}, []);

	// Parameters that should be excluded from the UI (handled by the backend)

	const currentParameters = useMemo(() => {
		if (!selectedProcedure) return [];
		return allParameters.filter(
			(p) =>
				p.FullName === selectedProcedure.FullName &&
				!BACKEND_MANAGED_PARAMS.includes(p.ParameterName.toLowerCase()),
		);
	}, [selectedProcedure, allParameters]);

	const handleProcedureChange = (proc: StoredProcedure | null) => {
		setSelectedProcedure(proc);
	};

	const handleExecute = async (formState: ParameterFormState[]) => {
		if (!selectedProcedure) return;

		setIsExecuting(true);
		setError("");

		const startTime = Date.now();

		// Build parameters array - only include checked parameters
		// Also filter out backend-managed params (@User) as a safety net
		const parameters: spParameter[] = formState
			.filter(
				(p) =>
					p.include &&
					!BACKEND_MANAGED_PARAMS.includes(p.parameterName.toLowerCase()),
			)
			.map((p) => ({
				name: p.parameterName,
				value: p.sendAsNull ? null : p.value,
			}));

		const resp = await executeStoredProcedure(
			selectedProcedure.FullName,
			parameters,
		);

		const duration = Date.now() - startTime;

		const newResult: ExecutionResult = {
			id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
			procedureName: selectedProcedure.FullName,
			parameters: parameters,
			timestamp: new Date(),
			duration: duration,
			data: resp.ok ? resp.data : null,
			isError: !resp.ok,
			errorMessage: resp.ok ? undefined : resp.message,
		};

		if (accumulateResults) {
			setExecutionResults((prev) => [newResult, ...prev]);
		} else {
			setExecutionResults([newResult]);
		}

		if (!resp.ok) {
			setError(resp.message);
		}

		setIsExecuting(false);
	};

	const handleDeleteResult = (id: string) => {
		setExecutionResults((prev) => prev.filter((r) => r.id !== id));
	};

	const handleClearAllResults = () => {
		setExecutionResults([]);
	};

	return (
		<PinGate>
			<HelmetDexteel title="SP Executor" />
			<Grid container spacing={2} sx={{ p: 2 }}>
				<Grid item xs={12}>
					<Typography variant="h5" sx={{ fontWeight: 600 }}>
						Stored Procedure Executor
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Select a stored procedure and configure its parameters to execute.
					</Typography>
				</Grid>

				<Grid item xs={12}>
					<Paper sx={{ p: 2 }}>
						<Grid container spacing={2} alignItems="center">
							<Grid item xs={12} md={9}>
								<SPAutocomplete
									procedures={procedures}
									value={selectedProcedure}
									onChange={handleProcedureChange}
									isLoading={isLoading}
								/>
							</Grid>
							<Grid item xs={12} md={3}>
								<FormControlLabel
									control={
										<Switch
											checked={accumulateResults}
											onChange={(e) => setAccumulateResults(e.target.checked)}
											color="primary"
										/>
									}
									label="Accumulate results"
								/>
							</Grid>
						</Grid>
					</Paper>
				</Grid>

				{selectedProcedure && (
					<Grid item xs={12}>
						<DynamicParameterForm
							parameters={currentParameters}
							onExecute={handleExecute}
							isExecuting={isExecuting}
						/>
					</Grid>
				)}

				<Grid item xs={12}>
					<ResultsList
						results={executionResults}
						onDeleteResult={handleDeleteResult}
						onClearAll={handleClearAllResults}
					/>
				</Grid>
			</Grid>

			<ErrorModal error={error} onHide={() => setError("")} />
		</PinGate>
	);
};
